import axios from "axios";
import moment from "moment";
import { classroomByPath } from "@/utils/userInfo";


const SUBJECT_COLOR_PALETTE = [
  { border: "rgb(255, 107, 107)",  bg: "rgba(255, 107, 107, 0.15)"  },
  { border: "rgb(78, 205, 196)",   bg: "rgba(78, 205, 196, 0.15)"   },
  { border: "rgb(69, 183, 209)",   bg: "rgba(69, 183, 209, 0.15)"   },
  { border: "rgb(255, 160, 122)",  bg: "rgba(255, 160, 122, 0.15)"  },
  { border: "rgb(152, 216, 200)",  bg: "rgba(152, 216, 200, 0.15)"  },
  { border: "rgb(247, 220, 111)",  bg: "rgba(247, 220, 111, 0.15)"  },
  { border: "rgb(187, 143, 206)",  bg: "rgba(187, 143, 206, 0.15)"  },
  { border: "rgb(133, 193, 233)",  bg: "rgba(133, 193, 233, 0.15)"  },
];
const EMPTY_COLOR = { border: "rgb(180, 180, 180)", bg: "rgba(180, 180, 180, 0.1)" };


function transformAgentEnergyData(agentDataArray, labelFormat) {
  const dataMap = {};
  const labelsSet = new Set();
  const subjectMap = {};

  if (!Array.isArray(agentDataArray)) return { dataMap, labelsSet, subjectMap };

  agentDataArray.forEach((entry) => {
    if (!entry.timestamp_start) return;
    const label = moment(entry.timestamp_start * 1000).format(labelFormat);
    labelsSet.add(label);
    dataMap[label] = entry.energy || 0;
    subjectMap[label] = entry.subject_name || "";
  });

  return { dataMap, labelsSet, subjectMap };
}


export async function fetchGraphData(
  { token },
  chartData,
  graphTimeRange,
  customRangeStart,
  customRangeEnd,
  path
) {
  const oneDay = 24 * 3600;
  const yearStart = Math.floor(new Date(new Date().getFullYear(), 0, 1).getTime() / 1000);
  let now = Math.floor(Date.now() / 1000);
  let from, granularity, labelFormat;

  switch (graphTimeRange) {
    case "Custom": {
      from = Math.floor(new Date(customRangeStart).setHours(0, 0, 0, 0) / 1000);
      const end = Math.floor(new Date(customRangeEnd).setHours(0, 0, 0, 0) / 1000);
      const duration = end - from;
      if (duration <= 0) return;
      if (duration <= oneDay)           { granularity = "block"; labelFormat = "DD.MM HH:mm"; }
      else if (duration <= 30 * oneDay) { granularity = "day";   labelFormat = "DD MMM";      }
      else                              { granularity = "month"; labelFormat = "MMM YYYY";    }
      now = end;
      break;
    }
    case "Deň":
      from = Math.floor(moment().startOf("day").valueOf() / 1000);
      granularity = "block";
      labelFormat = "DD.MM HH:mm";
      break;
    case "Týždeň":
      from = moment().subtract(7, "days").unix();
      granularity = "day";
      labelFormat = "DD MMM";
      break;
    case "Mesiac":
      from = moment().subtract(30, "days").unix();
      granularity = "day";
      labelFormat = "DD MMM";
      break;
    case "Rok":
    default:
      from = moment().startOf("year").unix();
      granularity = "month";
      labelFormat = "MMM";
      break;
  }

  // Validácia: startTimestamp nesmie byť menší ako začiatok roka
  if (from < yearStart) {
    from = yearStart;
  }

  const agent = classroomByPath[path];
  if (!agent) return;

  let labelsSet = new Set();
  let energyMap = {};
  let subjectMap = {};

  // === História spotreby ===
  const historyResponse = await axios.post(`${agent.agentUrl}/rest/history`, {
    startTimestamp: from,
    endTimestamp: now,
    granularity: granularity,
  });

  const historyData = historyResponse.data?.result;
  if (Array.isArray(historyData)) {
    const { dataMap, labelsSet: newLabels, subjectMap: newSubjectMap } =
      transformAgentEnergyData(historyData, labelFormat);
    newLabels.forEach((l) => labelsSet.add(l));
    Object.assign(energyMap, dataMap);
    Object.assign(subjectMap, newSubjectMap);
  }

  // === Zoradenie labelov ===
  const sortedLabels = Array.from(labelsSet).sort(
    (a, b) => moment(a, labelFormat).valueOf() - moment(b, labelFormat).valueOf()
  );

  // === Dataset pre každý predmet zvlášť ===
  const uniqueSubjects = [...new Set(
    Object.values(subjectMap).filter((s) => s && s !== "")
  )];

  const datasets = [];

  uniqueSubjects.forEach((subject, index) => {
    const color = SUBJECT_COLOR_PALETTE[index % SUBJECT_COLOR_PALETTE.length];

    const data = sortedLabels.map((label) =>
      subjectMap[label] === subject
        ? +(energyMap[label] ?? NaN).toFixed(3)
        : NaN
    );

    datasets.push({
      label: subject,
      data,
      borderColor: color.border,
      backgroundColor: color.bg,
      fill: false,
      tension: 0.3,
      spanGaps: false,
      pointRadius: 3,
      pointHoverRadius: 5,
    });
  });

  // === Dataset pre prázdnu učebňu ===
  const emptyData = sortedLabels.map((label) =>
    !subjectMap[label] || subjectMap[label] === ""
      ? +(energyMap[label] ?? NaN).toFixed(3)
      : NaN
  );

  if (emptyData.some((v) => !isNaN(v))) {
    datasets.unshift({
      label: granularity === "block" ? "Prázdna učebňa" : "Spotreba učebne",
      data: emptyData,
      borderColor: EMPTY_COLOR.border,
      backgroundColor: EMPTY_COLOR.bg,
      fill: false,
      tension: 0.3,
      spanGaps: false,
      pointRadius: 2,
      pointHoverRadius: 4,
      borderDash: granularity === "block" ? [3, 3] : [],
    });
  }

  // === Výsledok ===
  chartData.labels = sortedLabels;
  chartData.datasets = datasets;
  chartData.subjectMap = subjectMap;

  return JSON.parse(JSON.stringify(chartData));
}